import React, { useState, useEffect } from 'react';
import { Container, Grid, useTheme } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import classnames from 'classnames';

import { Block } from '@klp/design';
import { useKlpStyles } from '@klp/design/Theme';

import Image from './assets/Dronning_Eufemiasgate_10-009_logojustert.jpg';
import { LayoutProvider, useLayoutContext } from './context/LayoutContext';
import { Footer } from './Footer';
import { LayoutContext } from './LayoutContext';

const styles = (theme: Theme) => ({
    mainWrapper: {
        position: 'relative',
        zIndex: 0,
        display: 'flex',
        flex: '1 1 auto',
        margin: 0,
        padding: 0,
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '100vh',
    },
    mainWrapperDecorate: {
        position: 'relative',
        '&:after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            height: '15rem',
            width: '100%',
            background: theme.palette.primary.veryLight,
            zIndex: 0,
            left: 0,
            top: 0,
        },
    },
    bannerImage: {
        width: '100%',
        height: '15rem',
        objectFit: 'cover',
        objectPosition: 'center center',
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 1,
    },
    layoutContainer: {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flex: '1 1 auto',
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)':
            {
                minHeight: '100vh',
            },
    },
    sidebar: {
        minHeight: '100vh',
        transition: 'padding-left 0.25s linear',
    },
    sidebarClosed: {
        paddingLeft: theme.menu.width,
    },
    sidebarOpen: {
        paddingLeft: theme.menuOpen.width,
    },
    footerWrapper: {
        display: 'flex',
        flex: '0 0 auto',
    },
    fullScreen: {
        paddingTop: theme.topbar.height,
        minHeight: '100vh',
        // strech box in fullscreen mode
        '& > div:first-child': {
            minHeight: `calc(100vh - ${theme.topbar.height})`,
            padding: 0,
            maxWidth: '100vw',
        },
        // Hide footer in fullscreen mode
        '& > div:last-child': {
            display: 'none',
        },
    },
    withoutHeader: {
        '& .Header': {
            display: 'none',
        },
        paddingTop: 0,
    },
});

const LayoutContent: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const classes = useKlpStyles({}, styles);
    const theme = useTheme();

    // Context state
    const { isMobile, menuPin, setIsMobile } = useLayoutContext();

    // Local state for layout options
    const [decorate, setDecorate] = useState(false);
    const [withoutSidebar, setWithoutSidebar] = useState(false);
    const [withoutHeader, setWithoutHeader] = useState(false);
    const [withoutFooter, setWithoutFooter] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    const breakpointSm = theme.breakpoints.values.sm;

    useEffect(() => {
        const handleSetIsMobile = () => {
            setIsMobile(window.innerWidth < breakpointSm);
        };

        window.addEventListener('resize', handleSetIsMobile);
        return () => {
            window.removeEventListener('resize', handleSetIsMobile);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breakpointSm]);

    const classNames = classnames({
        [classes.mainWrapper]: true,
        [classes.mainWrapperDecorate]: decorate,
        [classes.sidebar]: !withoutSidebar && !isMobile,
        [classes.sidebarOpen]: !withoutSidebar && menuPin && !isMobile,
        [classes.sidebarClosed]: !withoutSidebar && !menuPin && !isMobile,
        [classes.fullScreen]: fullScreen,
        [classes.withoutHeader]: withoutHeader,
    });

    return (
        <LayoutContext.Provider
            value={{
                setDecorate,
                setWithoutSidebar,
                setFullScreen,
                setWithoutHeader,
                setWithoutFooter,
            }}
        >
            <Container className={classNames} maxWidth={false}>
                {decorate && (
                    <img
                        className={classes.bannerImage}
                        src={Image}
                        alt="KLP Banner Image"
                    />
                )}
                <Container
                    maxWidth={theme.layout.maxWidth}
                    className={classes.layoutContainer}
                >
                    <Grid container={true}>{children}</Grid>
                </Container>
                {!withoutFooter && (
                    <Block className={classes.footerWrapper}>
                        <Footer />
                    </Block>
                )}
            </Container>
        </LayoutContext.Provider>
    );
};

LayoutContent.displayName = 'LayoutContent';

export const Layout: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    return (
        <LayoutProvider>
            <LayoutContent>{children}</LayoutContent>
        </LayoutProvider>
    );
};

Layout.displayName = 'Layout';
